import { Map, Lock, Unlock, Grid3X3, ChevronDown, ChevronUp, ExternalLink, RotateCw, Sun, EyeOff, Eye, Plus, X, Layers } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useState } from 'react';

const GRID_COLORS = [
  { id: 'white', label: 'W', color: '255,255,255' },
  { id: 'gray', label: 'G', color: '128,128,128' },
  { id: 'black', label: 'B', color: '0,0,0' },
];

const SPOTLIGHT_MODES = [
  { id: 'spotlight', label: 'Spotlight', icon: Sun, colorClass: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { id: 'off', label: 'Blackout', icon: EyeOff, colorClass: 'bg-red-500/15 text-red-400 border-red-500/30' },
  { id: 'fullbright', label: 'Fullbright', icon: Eye, colorClass: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
];

export default function ControlPanel({
  radius, onRadiusChange,
  zoom, onZoomChange,
  fogOpacity, onFogOpacityChange,
  feather, onFeatherChange,
  gridSize, onGridSizeChange,
  gridColor, onGridColorChange,
  gridOpacity, onGridOpacityChange,
  spotlightMode, onSpotlightModeChange,
  locked, onLockedChange,
  showGrid, onShowGridChange,
  spotlight,
  onOpenMap,
  onOpenPlayerWindow,
  placedTokenCount,
  selectedToken,
  scenes, currentSceneId,
  onAddScene, onSwitchScene, onDeleteScene,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [newSceneName, setNewSceneName] = useState('');
  const [showSceneInput, setShowSceneInput] = useState(false);

  const currentMode = SPOTLIGHT_MODES.find(m => m.id === spotlightMode) || SPOTLIGHT_MODES[0];
  const cycleSpotlightMode = () => {
    const idx = SPOTLIGHT_MODES.findIndex(m => m.id === spotlightMode);
    const next = SPOTLIGHT_MODES[(idx + 1) % SPOTLIGHT_MODES.length];
    onSpotlightModeChange(next.id);
  };

  const handleAddScene = () => {
    if (!newSceneName.trim()) return;
    onAddScene(newSceneName.trim());
    setNewSceneName('');
    setShowSceneInput(false);
  };

  const ModeIcon = currentMode.icon;

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 glass-panel z-50 transition-all duration-300 ${
        collapsed ? 'w-auto' : 'w-[860px] max-w-[96vw]'
      }`}
      data-testid="control-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]" />
          <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest" style={{ fontFamily: 'Cinzel, serif' }}>
            DM Controls
          </span>
        </div>
        <div className="flex items-center gap-3">
          {selectedToken && (
            <span className="text-[10px] text-amber-400 font-mono bg-amber-500/10 px-2 py-0.5 rounded" data-testid="selected-token-info">
              <RotateCw className="w-3 h-3 inline mr-1" />
              {(selectedToken.rotation || 0)}deg / {((selectedToken.scale || 1) * 100).toFixed(0)}%
            </span>
          )}
          <span className="text-[10px] text-emerald-500/70 font-mono" data-testid="coords-display">
            {spotlight.x.toFixed(0)},{spotlight.y.toFixed(0)}
          </span>
          <button onClick={() => setCollapsed(!collapsed)} className="p-0.5 rounded hover:bg-white/10 text-zinc-500 hover:text-zinc-300" data-testid="toggle-panel-btn">
            {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-4 py-3 animate-fade-in">
          {/* Actions + Toggles Row */}
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" onClick={onOpenMap} className="bg-emerald-600 hover:bg-emerald-500 text-black font-semibold text-xs h-7 px-3" data-testid="load-map-btn">
              <Map className="w-3.5 h-3.5 mr-1" /> Map
            </Button>

            {/* Three-state Spotlight Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={cycleSpotlightMode}
                  className={`flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors border ${currentMode.colorClass}`}
                  data-testid="toggle-spotlight"
                >
                  <ModeIcon className="w-3.5 h-3.5" />
                  {currentMode.label}
                </button>
              </TooltipTrigger>
              <TooltipContent>Click to cycle: Spotlight / Blackout / Fullbright</TooltipContent>
            </Tooltip>

            {/* Lock */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onLockedChange(!locked)}
                  className={`flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
                    locked ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                  }`}
                  data-testid="toggle-lock"
                >
                  {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                  {locked ? 'Locked' : 'Lock'}
                </button>
              </TooltipTrigger>
              <TooltipContent>{locked ? 'Unlock spotlight movement' : 'Lock spotlight position'}</TooltipContent>
            </Tooltip>

            {/* Grid Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onShowGridChange(!showGrid)}
                  className={`flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
                    showGrid ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                  }`}
                  data-testid="toggle-grid"
                >
                  <Grid3X3 className="w-3.5 h-3.5" /> Grid
                </button>
              </TooltipTrigger>
              <TooltipContent>Toggle grid overlay</TooltipContent>
            </Tooltip>

            <div className="flex-1" />

            {placedTokenCount > 0 && (
              <span className="text-[10px] text-zinc-600 font-mono" data-testid="token-count">
                {placedTokenCount} token{placedTokenCount !== 1 ? 's' : ''}
              </span>
            )}

            <Button size="sm" onClick={onOpenPlayerWindow} className="bg-zinc-800 hover:bg-zinc-700 border border-emerald-500/30 text-emerald-400 text-xs h-7 px-3" data-testid="open-player-btn">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Player
            </Button>
          </div>

          {/* Scenes Row */}
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/5 overflow-x-auto" data-testid="scenes-row">
            <Layers className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
            {scenes && scenes.length > 0 && scenes.map(sc => (
              <button
                key={sc.id}
                onClick={() => onSwitchScene(sc.id)}
                className={`flex items-center gap-1 h-6 px-2 rounded text-[10px] font-medium whitespace-nowrap transition-colors shrink-0 ${
                  currentSceneId === sc.id
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                    : 'bg-zinc-800/60 text-zinc-400 border border-zinc-700 hover:border-zinc-500'
                }`}
                data-testid={`scene-chip-${sc.id}`}
              >
                {sc.name}
                <span
                  onClick={(e) => { e.stopPropagation(); onDeleteScene(sc.id); }}
                  className="ml-1 hover:text-red-400 cursor-pointer"
                >
                  <X className="w-2.5 h-2.5" />
                </span>
              </button>
            ))}
            {(!scenes || scenes.length === 0) && (
              <span className="text-[10px] text-zinc-600">No scenes</span>
            )}
            {showSceneInput ? (
              <div className="flex items-center gap-1 shrink-0">
                <input
                  value={newSceneName}
                  onChange={(e) => setNewSceneName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddScene(); if (e.key === 'Escape') setShowSceneInput(false); }}
                  placeholder="Scene name"
                  className="h-6 w-24 px-1.5 bg-zinc-900 border border-zinc-700 rounded text-[10px] text-white placeholder:text-zinc-600"
                  autoFocus
                  data-testid="new-scene-input"
                />
                <button onClick={handleAddScene} className="text-emerald-400 hover:text-emerald-300" data-testid="confirm-add-scene">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSceneInput(true)}
                className="h-6 px-2 rounded bg-zinc-800/60 text-zinc-500 border border-dashed border-zinc-700 hover:border-emerald-500/50 hover:text-emerald-400 text-[10px] shrink-0 transition-colors"
                data-testid="add-scene-btn"
              >
                <Plus className="w-3 h-3 inline mr-0.5" /> Scene
              </button>
            )}
          </div>

          {/* Sliders Grid */}
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3">
            <div className="control-group">
              <div className="control-label"><span>Size</span><span className="control-value">{radius}</span></div>
              <Slider value={[radius]} onValueChange={([v]) => onRadiusChange(v)} min={30} max={500} step={5} data-testid="slider-radius" />
            </div>
            <div className="control-group">
              <div className="control-label"><span>Zoom</span><span className="control-value">{zoom.toFixed(1)}x</span></div>
              <Slider value={[zoom * 10]} onValueChange={([v]) => onZoomChange(v / 10)} min={5} max={50} step={1} data-testid="slider-zoom" />
            </div>
            <div className="control-group">
              <div className="control-label"><span>Fog</span><span className="control-value">{(fogOpacity * 100).toFixed(0)}%</span></div>
              <Slider value={[fogOpacity * 100]} onValueChange={([v]) => onFogOpacityChange(v / 100)} min={0} max={100} step={1} data-testid="slider-fog" />
            </div>
            <div className="control-group">
              <div className="control-label"><span>Feather</span><span className="control-value">{feather}</span></div>
              <Slider value={[feather]} onValueChange={([v]) => onFeatherChange(v)} min={0} max={150} step={5} data-testid="slider-feather" />
            </div>
            <div className="control-group">
              <div className="control-label"><span>Grid</span><span className="control-value">{gridSize}px</span></div>
              <Slider value={[gridSize]} onValueChange={([v]) => onGridSizeChange(v)} min={10} max={200} step={5} data-testid="slider-grid-size" />
            </div>
            <div className="control-group">
              <div className="control-label"><span>G.Opa</span><span className="control-value">{(gridOpacity * 100).toFixed(0)}%</span></div>
              <Slider value={[gridOpacity * 100]} onValueChange={([v]) => onGridOpacityChange(v / 100)} min={1} max={100} step={1} data-testid="slider-grid-opacity" />
            </div>
          </div>

          {/* Grid Color Picker Row */}
          {showGrid && (
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/5">
              <span className="text-[10px] text-zinc-600 uppercase font-mono tracking-wider">Grid Color</span>
              <div className="flex gap-1" data-testid="grid-color-picker">
                {GRID_COLORS.map(gc => (
                  <button
                    key={gc.id}
                    onClick={() => onGridColorChange(gc.id)}
                    className={`w-6 h-6 rounded-md border-2 transition-all ${
                      gridColor === gc.id ? 'border-emerald-500 scale-110' : 'border-zinc-700 hover:border-zinc-500'
                    }`}
                    style={{ background: gc.id === 'white' ? '#fff' : gc.id === 'gray' ? '#888' : '#111' }}
                    data-testid={`grid-color-${gc.id}`}
                    title={gc.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
